import { Component, computed, DestroyRef, effect, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from '@angular/router';
import { ToggleButtonComponent } from "@controls/toggle-button/toggle-button.component";
import { Address } from "@models/address/address";
import { DocumentRecord } from '@models/annual-statements/document-record';
import { EntityType } from "@models/common/entity-type.enum";
import { Relationship } from '@models/relationships/relationship';
import { RelationshipTypeLabels } from "@models/relationships/relationship-type.enum";
import { TrustType } from '@models/trusts/trusts-enums';
import { IDetailTab } from '@models/ui-models/detail-tab';
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import { IShareholdersPatchModel } from "@pages/companies/models/company-shareholders-state.interface";
import {
  DocumentsGridComponent
} from "@pages/documents/components/documents-page/components/documents-grid/documents-grid.component";
import { TrustProfileStateService } from "@pages/trusts/services/trust-profile-state.service";
import { AlertsListComponent } from "@shared/components/alerts-list/alerts-list.component";
import { CommonPageComponent } from "@shared/components/commons/common-page/common-page.component";
import { LoaderComponent } from "@shared/components/commons/loader/loader.component";
import {
  EntityPendingChangesComponent
} from '@shared/components/entity-profiles/entity-pending-changes/entity-pending-changes.component';
import {
  EntityProfileTagsComponent
} from '@shared/components/entity-profiles/entity-profile-tags/entity-profile-tags.component';
import {
  EntitySecurityholdersViewComponent
} from "@shared/components/entity-profiles/entity-securityholders-view/entity-securityholders-view.component";
import { AgAddressesComponent } from "@shared/components/grid/ag-cells/common/ag-addresses/ag-addresses.component";
import { AgOpenActionComponent } from '@shared/components/grid/ag-cells/common/ag-open-action/ag-open-action.component';
import {
  AgOfficeholdersStatusComponent
} from "@shared/components/grid/ag-cells/trusts/ag-officeholders-status/ag-officeholders-status.component";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";
import { NotesComponent } from '@shared/components/notes/notes.component';
import { TabButtonComponent } from '@shared/controls/tab-button/tab-button.component';
import { HasRoleDirective } from '@shared/directives/has-role.directive';
import { getPageTitle } from "@shared/utils/get-page-title";
import { getQuantityLabel } from "@shared/utils/get-quantity-label";
import { Guid } from '@shared/utils/guid.helper';
import { getCompanyProfilePath, getIndividualProfilePath } from "@shared/utils/route-path-utils";
import { ColDef, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { TrustDetailsComponent } from './components/trust-details/trust-details.component';
import { TrustProfileCardComponent } from './components/trust-profile-card/trust-profile-card.component';

export type TrustProfileTabs =
  'Details'
  | 'Trustee'
  | 'Beneficiaries'
  | 'Relationships'
  | 'Documents'
  | 'UnitHolders'
  | 'Unitholders';

@Component({
  selector: 'app-trust-profile',
  standalone: true,
  imports: [
    TrustProfileCardComponent,
    EntityProfileTagsComponent,
    EntityPendingChangesComponent,
    TabButtonComponent,
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    NgbDropdownItem,
    TrustDetailsComponent,
    LoaderComponent,
    NotesComponent,
    AlertsListComponent,
    HasRoleDirective,
    ListGridClientSideComponent,
    ToggleButtonComponent,
    EntitySecurityholdersViewComponent,
    DocumentsGridComponent
  ],
  providers: [TrustProfileStateService],
  templateUrl: 'trust-profile-page.component.html',
  styleUrl: 'trust-profile-page.component.scss',
})
export class TrustProfilePageComponent extends CommonPageComponent implements OnInit {
  private trustProfileStateService = inject(TrustProfileStateService);

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private titleService = inject(Title);
  private destroyRef = inject(DestroyRef);

  readonly EntityType = EntityType;
  readonly TrustType = TrustType;

  modalOpened = this.trustProfileStateService.modalOpened;
  isTrustLoading = this.trustProfileStateService.state.loaders.isTrustLoading;
  isDocumentsLoading = this.trustProfileStateService.state.loaders.isPendingDocumentsLoading;
  isShareholdersLoading = this.trustProfileStateService.state.loaders.isShareholdersLoading;

  isPendingChangesVisible = this.trustProfileStateService.state.isPendingChangesVisible;
  isXpmConnected = this.trustProfileStateService.state.isXpmConnected;
  tabs = this.trustProfileStateService.tabs;

  trustViewModel = this.trustProfileStateService.trustViewModel;
  trustWithPendingChanges = this.trustProfileStateService.state.trustWithPendingChanges;

  unitholdersBeneficiaryViewModel = this.trustProfileStateService.unitholdersBeneficiaryViewModel;

  pendingDocuments = this.trustProfileStateService.state.pendingDocumentRecords;
  trustRelationships = this.trustProfileStateService.trustRelationships;

  activeTab = signal<TrustProfileTabs>('Details');
  isTabsHidden = signal<boolean>(false);

  // Notes
  notesTotal = this.trustProfileStateService.state.notesTotal;
  notesVisible = signal<boolean>(false);
  notesLabel = computed(() => getQuantityLabel(this.notesTotal()));

  // Alerts
  trustAlerts = this.trustProfileStateService.trustAlerts;
  alertsVisible = signal<boolean>(false);
  alertsLabel = computed(() => getQuantityLabel(this.trustAlerts().length, 'Alert'));

  constructor() {
    super();

    effect(() => {
      const trustName = this.trustWithPendingChanges().name;

      if (trustName) {
        this.titleService.setTitle(getPageTitle(trustName));
        this.headerService.setHeaderData({
          title: trustName,
          subtitle: 'Trust',
          iconClass: 'ic-trusts-20'
        });
      }
    });
  }

  override ngOnInit() {
    const trustId = this.route.snapshot.paramMap.get('id');

    if (!trustId || !Guid.isValidGuid(trustId)) {
      void this.router.navigate(['/trusts']);
      return;
    }

    this.trustProfileStateService.init(trustId);
    this.headerService.setHeaderLoading();

    this.updateColumns();
    this.listenActiveTabQueryParam();
  }

  onTrustRefreshedEvent() {
    this.trustProfileStateService.loadTrust();
  }

  selectItems(items: Relationship[]): void {
    this.checkedOnItems = items;
  }

  // =====  Trust Details  =====

  openEditTrustDetailsModal(): void {
    this.trustProfileStateService.openEditTrustDetailsModal();
  }

  setIsTabsHidden(isTabsHidden: boolean) {
    this.isTabsHidden.set(isTabsHidden);
  }

  patchShareholdersViewModel(shareholdersPatchModel: IShareholdersPatchModel): void {
    this.trustProfileStateService.patchShareholdersState(shareholdersPatchModel);
    this.setIsTabsHidden(!!shareholdersPatchModel.shareholderDetails);
  }

  setIsPastRelationshipsVisible(isPastRelationshipsVisible: boolean): void {
    this.trustProfileStateService.setIsPastRelationshipsVisible(isPastRelationshipsVisible);
  }

  deleteDocumentChange(documentRecord: DocumentRecord): void {
    this.trustProfileStateService.openDeleteDocumentModal([documentRecord]);
  }

  onNotesTotalChanged(change: number): void {
    this.trustProfileStateService.setNotesTotal(this.notesTotal() + change);
  }

  setIsPendingChangesVisible(isPendingChangesVisible: boolean): void {
    this.trustProfileStateService.setIsPendingChangesVisible(isPendingChangesVisible);
  }

  // =====  Query Params Related  =====

  static activeTabQueryParamKey = 'tab';

  setActiveTab(tab: IDetailTab<TrustProfileTabs>): void {
    const queryTabName = tab.title != 'Details' ? tab.title : null;
    const queryParams = { [TrustProfilePageComponent.activeTabQueryParamKey]: queryTabName };
    this.router.navigate([], { queryParams, queryParamsHandling: 'merge' });
  }

  listenActiveTabQueryParam(): void {
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        if (!this) return;

        this.checkedOnItems = [];
        const tabName = params[TrustProfilePageComponent.activeTabQueryParamKey] as TrustProfileTabs | null;

        if (!tabName || !this.tabs().find((allowedTab) => allowedTab.title == tabName)) {
          this.activeTab.set('Details');
          this.trustProfileStateService.onSwitchOnTab('Details');
          return;
        }

        if (this.activeTab() != 'Unitholders') {
          this.updateColumns();
        }

        this.trustProfileStateService.onSwitchOnTab(tabName);
        this.activeTab.set(tabName);
      });
  }

  // =====  Forms  =====

  openTrustDistributionModal(): void {
    this.trustProfileStateService.openTrustDistributionModal();
  }

  changeTrustee(): void {
    this.trustProfileStateService.changeTrustee();
  }

  changeAppointor(): void {
    this.trustProfileStateService.changeAppointor();
  }

  // =====  Grids and related  =====

  colDefs: ColDef[] = [];
  relationshipColDefs: ColDef[] = [];
  checkedOnItems: Relationship[] = [];

  updateColumns() {
    this.relationshipColDefs = [
      {
        headerName: 'Name',
        valueGetter: (params: ValueGetterParams<Relationship>) => this.getRelationshipName(params.data!),
        width: 150,
        flex: 2,
        editable: false,
      },
      {
        headerName: 'Position',
        field: 'type',
        width: 180,
        flex: 1,
        valueFormatter: (params: ValueFormatterParams<Relationship>) => RelationshipTypeLabels[params.data?.type!] || ''
      },
      {
        headerName: 'Status',
        field: 'end',
        width: 130,
        flex: 1,
        cellRenderer: AgOfficeholdersStatusComponent,
      },
      {
        headerName: 'Address',
        valueGetter: (params: ValueGetterParams<Relationship>) => this.getRelationshipAddress(params.data!),
        cellRenderer: AgAddressesComponent,
        editable: false,
        cellClass: 'h-100 d-flex align-items-center',
        width: 150,
        flex: 1,
      },
      {
        field: '',
        width: 50,
        suppressHeaderMenuButton: true,
        suppressMovable: true,
        resizable: false,
        sortable: false,
        cellClass: 'actions-button-cell',

        cellRenderer: AgOpenActionComponent,
        cellRendererParams: {
          hidden: (params) => !!params.data.entityDataOverride,
          open: this.openProfile.bind(this),
        }
      }
    ];

    this.colDefs = [
      {
        headerName: 'Name',
        valueGetter: (params: ValueGetterParams<Relationship>) => this.getRelationshipName(params.data!),
        width: 150,
        flex: 2,
        editable: false,
      },
      {
        headerName: 'Position',
        field: 'type',
        width: 100,
        initialHide: this.activeTab() != 'Relationships',
        flex: 1,
        valueFormatter: (params: ValueFormatterParams<Relationship>) => RelationshipTypeLabels[params.data?.type!] ?? '-'
      },
      {
        headerName: 'Address',
        valueGetter: (params: ValueGetterParams<Relationship>) => this.getRelationshipAddress(params.data!),
        cellRenderer: AgAddressesComponent,
        cellClass: 'h-100 d-flex align-items-center',
        editable: false,
        width: 150,
        flex: 1,
      },
      {
        field: '',
        width: 50,
        suppressHeaderMenuButton: true,
        suppressMovable: true,
        resizable: false,
        sortable: false,
        cellClass: 'actions-button-cell',

        cellRenderer: AgOpenActionComponent,
        cellRendererParams: {
          hidden: (params) => !!params.data.entityDataOverride,
          open: this.openProfile.bind(this),
        }
      }
    ];
  }

  openProfile(relationship: Relationship) {
    if (relationship) {
      if (relationship.relatedEntityId && relationship.relatedEntityId != Guid.EmptyGuid) {
        this.router.navigate(getCompanyProfilePath(relationship.entityId));
      }

      if (relationship.individualId && relationship.individualId != Guid.EmptyGuid) {
        this.router.navigate(getIndividualProfilePath(relationship.individualId));
      }
    }
  }

  getRelationshipAddress(relationship: Relationship): (Address | null)[] {
    if (relationship && relationship.entityDataOverride && relationship.entityDataOverride.registeredAddress) {
      return [relationship.entityDataOverride.registeredAddress];
    }

    if (relationship && relationship.individualDataOverride && relationship.individualDataOverride.address) {
      return [relationship.individualDataOverride.address];
    }

    return [null];
  }

  getRelationshipName(relationship: Relationship): string {
    if (relationship && relationship.entityDataOverride && relationship.entityDataOverride.name) {
      return relationship.entityDataOverride.name;
    }

    if (relationship && relationship.individualDataOverride && relationship.individualDataOverride.fullNameLabel) {
      return relationship.individualDataOverride.fullNameLabel;
    }
    return '-';
  }
}
